import { ReactNode, useMemo } from "react";
import { useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { IconChevronRight } from "@tabler/icons-react";
import { AnyPeriod, classNames, toGQLPeriod, useAppConfig } from "@ct-react/core";
import { useLocaleContext, useLocaleUrl } from "@ct-react/locale";
import { MailInput } from "@shared/graphql";
import { actions, form } from "../i18n/sharable-defs";
import { useTools } from "../hooks/tools";
import { useForm, validators } from "../hooks/form";
import Button from "./common/button";
import CheckboxInput from "./inputs/checkbox";
import "./contact-form.scss";

const SEND_MAIL_TO_AGENCY_GQL_MUT = gql`
  mutation MailAgency($agencyId: ID!, $request: MailRequestInput!) {
    mailRelatedContact(merchantId: $agencyId, request: $request) @recaptcha(action: "mail_form")
  }
`;

type MailData = MailInput & { legal: boolean };

type Props = {
  className?: string;
  data?: { articleId: string, period?: AnyPeriod }
};

const ContactForm = ({ className, data: related }: Props) => {

  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { baseUrl, options: { agencyId } } = useAppConfig();
  const { currentUrl } = useLocaleUrl(baseUrl);
  const { mutation } = useTools();

  const { register, formState, handleSubmit, patch } = useForm<MailData>({ legal: false });
  const policyPath = useMemo(() => `/conditions/policy-${locale.basename.toLowerCase()}.pdf`, [ locale ]);

  const [ sendMail ] = useMutation(SEND_MAIL_TO_AGENCY_GQL_MUT);

  const submit = async ({ legal, ...data }: Partial<MailData>) => {
    const variables = {
      agencyId,
      request: {
        ...data,
        ...(!!related) && {
          body: `${data.body}\n\nVoir l'article: ${currentUrl}`,
          article: {
            ...related,
            ...(!!related.period) && { period: toGQLPeriod(related.period) }
          }
        }
      }
    };
    await mutation.withRecaptcha(sendMail, "mail_form")({ variables })
      .then(() => patch(true));
  }

  const wrapperClasses = classNames("contact-form", className);

  return (
    <form className={wrapperClasses}
          onSubmit={handleSubmit(submit, { resetStateSubmitTimer: 5000 })}>
      <div className="f-group">
        <label htmlFor="firstName">{`${intl.formatMessage(form.firstName)}*`}</label>
        <input type="text" id="firstName"
               className={classNames({ dirty: "contact.firstName" in formState.dirtyFields })}
               {...register("contact.firstName", { required: intl.formatMessage(form.emptyError) })}
               placeholder={intl.formatMessage(form.firstName)}
               autoComplete="given-name" />
        <p className="f-error">
          {!!formState.dirtyFields["contact.firstName"] && formState.dirtyFields["contact.firstName"].error}
        </p>
      </div>
      <div className="f-group">
        <label htmlFor="lastName">{`${intl.formatMessage(form.lastName)}*`}</label>
        <input type="text" id="lastName"
               className={classNames({ dirty: "contact.lastName" in formState.dirtyFields })}
               {...register("contact.lastName", { required: intl.formatMessage(form.emptyError) })}
               placeholder={intl.formatMessage(form.lastName)}
               autoComplete="family-name" />
        <p className="f-error">
          {!!formState.dirtyFields["contact.lastName"] && formState.dirtyFields["contact.lastName"].error}
        </p>
      </div>
      <div className="f-group">
        <label htmlFor="email">{`${intl.formatMessage(form.email)}*`}</label>
        <input type="email" id="email"
               className={classNames({ dirty: "contact.email" in formState.dirtyFields })}
               {...register("contact.email", {
                 required: intl.formatMessage(form.emptyError),
                 validators: { ...validators.emailFormat(intl.formatMessage(form.emailError)) }
               })}
               placeholder={intl.formatMessage(form.email)}
               autoComplete="email" />
        <p className="f-error">
          {!!formState.dirtyFields["contact.email"] && formState.dirtyFields["contact.email"].error}
        </p>
      </div>
      <div className="f-group">
        <label htmlFor="phone">{`${intl.formatMessage(form.phone)}*`}</label>
        <input type="tel" id="phone"
               className={classNames({ dirty: "contact.phone" in formState.dirtyFields })}
               {...register("contact.phone", {
                 required: intl.formatMessage(form.emptyError),
                 validators: { ...validators.phoneFormat(intl.formatMessage(form.phoneError)) }
               })}
               placeholder={intl.formatMessage(form.phone)}
               autoComplete="tel" />
        <p className="f-error">
          {!!formState.dirtyFields["contact.phone"] && formState.dirtyFields["contact.phone"].error}
        </p>
      </div>
      <div className="f-group">
        <label htmlFor="phone">{`${intl.formatMessage(form.msg)}*`}</label>
        <textarea id="msg"
                  className={classNames({ dirty: "body" in formState.dirtyFields })}
                  {...register("body", {
                    required: intl.formatMessage(form.emptyError),
                    validators: { ...validators.minLength(20, intl.formatMessage(form.minError, { min: 20 }) )}
                  })}
                  placeholder={intl.formatMessage(form.msgPlaceholder)}
                  rows={5} />
        <p className="f-error">
          {!!formState.dirtyFields["body"] && formState.dirtyFields["body"].error}
        </p>
      </div>
      <div className="f-group no-error">
        {/*@ts-ignore*/}
        <CheckboxInput id="legal"
                       label={intl.formatMessage(form.legal, { link: (chunks: ReactNode) =>
                           (<a key="link" href={policyPath} target="_blank">{chunks}</a>)}
                       )}
                       {...register<boolean>("legal", { required: intl.formatMessage(form.emptyError) })} />
      </div>
      <div className="submitter">
        <Button type="submit"
                className="cream bold with-icon"
                processing={formState.submit.inProcess}
                disabled={!formState.isValid}>
          {intl.formatMessage(actions.send)}
          <IconChevronRight />
        </Button>
        {formState.submit.finished &&
          <span className={classNames("callback", { error: !formState.submit.succeeded })}>
            {intl.formatMessage(formState.submit.succeeded ? form.sent : form.failed)}
          </span>
        }
      </div>
    </form>);

};

export default ContactForm;
